import { useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { DarkModeToggle } from '@components/dark-mode-toggle';
import { DevModeToggle } from '@components/dev-mode-toggle';
import { Divider } from '@components/divider';
import { IconDoubleArrow } from '@components/icons/icon-double-arrow';
import { IconExpand } from '@components/icons/icon-expand';
import { IconSettings } from '@components/icons/icon-settings';
import { ImpersonateUserModal } from '@components/impersonate-user';
import { NavItem } from '@components/layout/layout.type';
import { OptimizedImage } from '@components/optimized-image';
import { ProtectedComponent } from '@components/protected-component';
import { SidebarItem } from '@components/sidebar-item';
import { ENABLED_COMPANY_INFO_ROUTE, ENABLED_USERS_ROUTE } from '@constants/company-auth-permissions';
import { PLACEHOLDER_IMAGE } from '@constants/global';
import { COMPANY_INFO_ROUTE, SUPPORT_ROUTE, USERS_ROUTE } from '@constants/routes';
import { AuthContext } from '@contexts/auth/auth-context';
import { CompanyClubContext } from '@contexts/company-club/company-club-context';
import { NavigationContext } from '@contexts/navigation/navigation-context';
import { UserContext } from '@contexts/user/user-context';
import { useIsProtectedElement } from '@hooks/use-is-protected-element';
import classNames from 'classnames';

import { Props } from './sidebar-desktop.type';

export const SidebarDesktop = ({ isCollapsed, handleIsVisible, handleCompanyModal }: Props) => {
  const { userState } = useContext(UserContext);
  const { navigationItems } = useContext(NavigationContext);
  const { i18n } = useTranslation();
  const { companyState, clubState } = useContext(CompanyClubContext);
  const { isImpersonated } = useContext(AuthContext);
  const { t } = useTranslation();
  const [isImpersonateModalOpen, setIsImpersonateModalOpen] = useState(false);
  const navigate = useNavigate();

  const SUPPORT_ROUTE_WITH_LOCALE = useMemo(() => {
    const language = i18n.language;
    if (language === 'ca') {
      return `${SUPPORT_ROUTE}/es`;
    }
    return `${SUPPORT_ROUTE}/${language}`;
  }, [i18n.language]);

  const handleClick = (href: string) => {
    globalThis.open(href, '_blank');
  };

  const handleClickCompany = () => {
    if (handleCompanyModal) handleCompanyModal(true);
  };

  const handleClickUser = () => {
    setIsImpersonateModalOpen(true);
  };

  const isEnabledCompanyInfo = useIsProtectedElement({
    permissions: ENABLED_COMPANY_INFO_ROUTE,
    checkEveryPermission: false,
  });

  const handleClickCompanyInfo = () => {
    if (isEnabledCompanyInfo) {
      navigate(COMPANY_INFO_ROUTE.replace(':companyId', companyState.legacyId).replace(':clubSlug', clubState.slug));
      return;
    }
    navigate(USERS_ROUTE.replace(':companyId', companyState.legacyId).replace(':clubSlug', clubState.slug));
  };

  return (
    <>
      <aside
        className={classNames(
          {
            'lg:w-24': isCollapsed,
            'lg:w-64': !isCollapsed,
          },
          'pt-3 pb-4 pl-6 pr-4',
          'flex flex-col transition-width duration-500 ease-in-out justify-between overflow-x-hidden sm:bg-secondary'
        )}>
        <nav className='flex flex-col place-items-start gap-y-3 xs:flex-1 overflow-y-auto overflow-x-hidden'>
          {navigationItems.map(({ name, ...navItem }: NavItem) => (
            <SidebarItem
              key={name}
              dataTestId={`sidebar-item--${name}`}
              name={t(`menu:${name}`)}
              {...navItem}
              isCollapsed={isCollapsed}
              isImpersonatedUser={isImpersonated}
              userEmail={userState?.email}
            />
          ))}
        </nav>
        <Divider className='xs:hidden w-full h-1' />
        <div className='flex-col mb-10'>
          <div className='flex shrink-0 p-3 items-center rounded-sm w-full'>
            <OptimizedImage
              className='rounded-sm w-8 h-8'
              src={companyState.logoUrl ?? ''}
              width={64}
              quality={80}
              alt='company'
            />
            {!isCollapsed && (
              <>
                <span className='text-secondary text-body-m ml-3 line-clamp-2 text-left'>{companyState.name}</span>

                <div className='flex'>
                  <ProtectedComponent
                    permissions={[...ENABLED_COMPANY_INFO_ROUTE, ...ENABLED_USERS_ROUTE]}
                    checkEveryPermission={false}>
                    <button
                      className='group hover:fill-secondary-active rounded-full p-1'
                      onClick={handleClickCompanyInfo}>
                      <IconSettings className='w-6 h-6 fill-none text-secondary' />
                    </button>
                  </ProtectedComponent>
                  <button className='group hover:fill-secondary-active rounded-full p-1' onClick={handleClickCompany}>
                    <IconExpand className='w-6 h-6 fill-none text-secondary' />
                  </button>
                </div>
              </>
            )}
          </div>
          {userState && (
            <button
              onClick={handleClickUser}
              className='flex shrink-0 gap-3 p-3 items-center rounded-sm w-full group hover:fill-secondary-active mt-2'>
              <OptimizedImage
                className='w-8 h-8 rounded-full'
                src={userState.profileImageUrl ?? PLACEHOLDER_IMAGE}
                width={64}
                quality={80}
                alt='profile'
              />
              {!isCollapsed && (
                <div className='overflow-x-hidden'>
                  <p className='text-secondary text-left text-body-m mb-1 truncate'>
                    {userState.firstName} {userState.lastName}{' '}
                  </p>
                  <p className='text-left text-body-xs text-tertiary truncate'>{userState.email}</p>
                </div>
              )}
            </button>
          )}
        </div>
        <div className={classNames('mx-auto xs:mx-0 flex gap-4 px-3 items-center', { 'justify-center': isCollapsed })}>
          {!isCollapsed && (
            <>
              <DarkModeToggle className='items-center' />
              <DevModeToggle />
              <button
                className='w-6 h-6 px-1 py-1  rounded-full justify-center items-center gap-2 inline-flex fill-tertiary'
                onClick={() => handleClick(SUPPORT_ROUTE_WITH_LOCALE)}>
                <span className='text-primary text-headline-xs'>?</span>
              </button>
            </>
          )}
          {handleIsVisible && (
            <button
              className={classNames('w-6 h-6 flex items-center justify-center', {
                'ml-auto': !isCollapsed,
              })}
              onClick={handleIsVisible}
              aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}>
              <IconDoubleArrow
                className={classNames({ 'rotate-180': !isCollapsed }, 'w-6 h-6 transition-all text-secondary')}
                aria-hidden
              />
            </button>
          )}
        </div>
      </aside>

      <ImpersonateUserModal
        isImpersonateModalOpen={isImpersonateModalOpen}
        setIsImpersonateModalOpen={setIsImpersonateModalOpen}
      />
    </>
  );
};
